import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import toast from 'react-hot-toast';

const BASE_URL = window.location.origin;

const DOC_TYPES = [
  { id: 1, name: 'Aadhaar Card',      icon: '🪪', hint: 'Upload front side of Aadhaar' },
  { id: 2, name: 'PAN Card',          icon: '💳', hint: 'Income Tax PAN card' },
  { id: 3, name: 'Skill Certificate', icon: '📜', hint: 'ITI / vocational training certificate' },
  { id: 8, name: 'Bank Passbook',     icon: '🏦', hint: 'First page showing account details' },
];

const STATUS_BADGE = {
  approved: 'bg-green-100 text-green-700',
  pending:  'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700',
};

function imgSrc(url) {
  if (!url) return null;
  return url; // proxy routes /uploads/... → backend:5000
}

/* ─── Professional ID Card ─────────────────────────────────────────── */
function IDCard({ data, photoUrl }) {
  const age = data.age ?? (data.dob
    ? Math.floor((Date.now() - new Date(data.dob)) / (365.25 * 24 * 3600 * 1000))
    : null);
  const isVerified = data.verification_status === 'verified';
  const validTill  = data.expiry_date
    ? new Date(data.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const skills = (data.skills || []).map(s => typeof s === 'string' ? s : (s.skill_name || s.name)).filter(Boolean);
  const langs  = (data.languages || []).map(l => typeof l === 'string' ? l : (l.language_name || l.name)).filter(Boolean);

  return (
    <div className="w-full max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl border border-blue-900/20 select-none">

      {/* Header */}
      <div className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 px-4 pt-4 pb-3 overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-4 w-16 h-16 bg-white/5 rounded-full translate-y-1/2 pointer-events-none" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow">
              <span className="text-blue-800 font-extrabold text-sm leading-none">NH</span>
            </div>
            <div>
              <p className="text-white font-extrabold text-[13px] tracking-wide leading-none">naukarihaat.com</p>
              <p className="text-blue-200 text-[9px] tracking-widest uppercase mt-0.5">Worker Identity Card</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            {isVerified ? (
              <span className="inline-flex items-center gap-1 bg-green-400 text-green-900 text-[9px] font-extrabold px-2 py-0.5 rounded-full">
                <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                VERIFIED
              </span>
            ) : (
              <span className="inline-flex items-center bg-yellow-400 text-yellow-900 text-[9px] font-extrabold px-2 py-0.5 rounded-full">PENDING</span>
            )}
            <p className="text-blue-200 text-[8px]">Valid till {validTill}</p>
          </div>
        </div>
      </div>

      {/* Color stripe */}
      <div className="h-1.5 bg-gradient-to-r from-blue-500 via-blue-400 to-sky-400" />

      {/* Body */}
      <div className="bg-white px-4 py-4 space-y-3">
        {/* Photo + Info */}
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-20 h-24 rounded-xl border-2 border-blue-700 overflow-hidden bg-blue-50 shadow-md relative">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Worker"
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-4xl font-extrabold text-blue-200">
                    {data.full_name?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-1.5">
            <h2 className="font-extrabold text-gray-900 text-sm uppercase tracking-wide leading-tight">
              {data.full_name}
            </h2>
            <table className="text-[10px] text-gray-600 w-full">
              <tbody>
                {age && (
                  <tr>
                    <td className="text-gray-400 pr-1.5 pb-0.5 whitespace-nowrap">Age</td>
                    <td className="font-semibold text-gray-800">{age} Years</td>
                  </tr>
                )}
                {data.gender && (
                  <tr>
                    <td className="text-gray-400 pr-1.5 pb-0.5">Gender</td>
                    <td className="font-semibold text-gray-800 capitalize">{data.gender}</td>
                  </tr>
                )}
                {data.experience_years != null && (
                  <tr>
                    <td className="text-gray-400 pr-1.5 pb-0.5">Exp.</td>
                    <td className="font-semibold text-gray-800">{data.experience_years} yr{data.experience_years !== 1 ? 's' : ''}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {(data.city_village || data.district || data.state) && (
              <div className="flex items-start gap-1">
                <span className="text-[10px] mt-px">📍</span>
                <p className="text-[10px] text-gray-600 leading-tight">
                  {[data.city_village, data.district, data.state].filter(Boolean).join(', ')}
                  {data.pincode ? ` – ${data.pincode}` : ''}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-dashed border-gray-200" />

        {skills.length > 0 && (
          <div>
            <p className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">Skills</p>
            <div className="flex flex-wrap gap-1">
              {skills.slice(0, 8).map(s => (
                <span key={s} className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">{s}</span>
              ))}
            </div>
          </div>
        )}

        {langs.length > 0 && (
          <div>
            <p className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">Languages</p>
            <div className="flex flex-wrap gap-1">
              {langs.map(l => (
                <span key={l} className="text-[10px] bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full">{l}</span>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-dashed border-gray-200" />

        {/* Footer row */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">Registration No.</p>
            <p className="text-[11px] font-mono font-bold text-gray-800 tracking-wider mt-0.5">{data.registration_number}</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
              <svg viewBox="0 0 24 24" className="w-7 h-7 text-gray-400" fill="currentColor">
                <path d="M3 3h7v7H3V3zm2 2v3h3V5H5zm9-2h7v7h-7V3zm2 2v3h3V5h-3zM3 14h7v7H3v-7zm2 2v3h3v-3H5zm9-2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h2v2h-2v-2zm-4 4h2v2h-2v-2zm2 2h2v-2h2v4h-4v-2zm2-6h2v2h-2v-2z"/>
              </svg>
            </div>
            <p className="text-[7px] text-gray-400 mt-0.5">Scan to verify</p>
          </div>
        </div>
      </div>

      {/* Card footer */}
      <div className="bg-blue-900 px-4 py-2 flex items-center justify-between">
        <p className="text-blue-300 text-[8px] tracking-wide">www.naukarihaat.com</p>
        <p className="text-blue-400 text-[8px]">Your bridge to better work</p>
      </div>
    </div>
  );
}

/* ─── Documents Tab ─────────────────────────────────────────── */
function DocumentsSection() {
  const [docs, setDocs]           = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const fileRefs = useRef({});

  const fetchDocs = useCallback(() => {
    api.get('/worker/profile/documents')
      .then(r => setDocs(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingDocs(false));
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  async function handleUpload(e, docTypeId) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(docTypeId);
    const form = new FormData();
    form.append('document', file);
    form.append('doc_type_id', docTypeId);
    try {
      await api.post('/worker/profile/documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      fetchDocs();
    } catch {} finally {
      setUploading(null);
      if (fileRefs.current[docTypeId]) fileRefs.current[docTypeId].value = '';
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm('Delete this document?')) return;
    setDeleting(docId);
    try {
      await api.delete(`/worker/profile/documents/${docId}`);
      toast.success('Document removed');
      setDocs(d => d.filter(x => x.id !== docId));
    } catch {} finally {
      setDeleting(null);
    }
  }

  if (loadingDocs) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const byType = {};
  docs.forEach(d => {
    if (!byType[d.doc_type_id]) byType[d.doc_type_id] = [];
    byType[d.doc_type_id].push(d);
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-bold text-gray-900">Your Documents</h2>
        <p className="text-sm text-gray-500 mt-0.5">Upload documents to boost your profile trust score</p>
      </div>

      {DOC_TYPES.map(dt => {
        const uploaded    = byType[dt.id] || [];
        const isUploading = uploading === dt.id;

        return (
          <div key={dt.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{dt.icon}</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{dt.name}</p>
                  <p className="text-xs text-gray-400">{dt.hint}</p>
                </div>
              </div>
              <div>
                <input
                  ref={el => fileRefs.current[dt.id] = el}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={e => handleUpload(e, dt.id)}
                />
                <button
                  onClick={() => fileRefs.current[dt.id]?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                >
                  {isUploading ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>+ {uploaded.length > 0 ? 'Add More' : 'Upload'}</>
                  )}
                </button>
              </div>
            </div>

            {uploaded.length > 0 ? (
              <div className="space-y-2">
                {uploaded.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base flex-shrink-0">
                        {doc.mime_type?.includes('pdf') ? '📄' : '🖼️'}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate max-w-[150px]">{doc.file_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-px rounded ${STATUS_BADGE[doc.status] || STATUS_BADGE.pending}`}>
                            {doc.status}
                          </span>
                          {doc.file_size_kb && (
                            <span className="text-[9px] text-gray-400">{doc.file_size_kb} KB</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-600 hover:underline font-medium"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleting === doc.id}
                        className="text-[11px] text-red-500 hover:text-red-700 font-medium disabled:opacity-40"
                      >
                        {deleting === doc.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 rounded-lg px-4 py-3 text-center">
                <p className="text-xs text-gray-400">No document uploaded yet</p>
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <p className="text-xs text-blue-800 font-semibold">Why upload documents?</p>
        <ul className="mt-1 space-y-1 text-xs text-blue-600 list-disc list-inside">
          <li>Employers trust verified profiles more</li>
          <li>Priority placement in job matching</li>
          <li>Faster shortlisting by contractors</li>
        </ul>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────── */
export default function WorkerDigitalCard() {
  const navigate   = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('card');
  const [uploading, setUploading] = useState(false);
  const [photoVer, setPhotoVer] = useState(Date.now()); // cache-bust after upload
  const photoInput = useRef(null);

  useEffect(() => {
    api.get('/worker/profile')
      .then(r => setData(r.data.data))
      .catch(() => navigate('/worker/dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const cardUrl = data ? `${BASE_URL}/w/${data.registration_number}` : '';

  function copyLink() {
    navigator.clipboard.writeText(cardUrl)
      .then(() => toast.success('Card link copied!'))
      .catch(() => toast.error('Could not copy link'));
  }

  async function shareCard() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${data.full_name} – Worker Card | naukarihaat.com`,
          text: `Verified worker profile of ${data.full_name}.`,
          url: cardUrl,
        });
      } catch {}
    } else {
      copyLink();
    }
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('photo', file);
    try {
      const uploadRes = await api.post('/worker/profile/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = uploadRes.data.data?.url;
      // Update photo_url directly so card re-renders immediately, then bump cache-bust key
      setData(d => ({ ...d, profile_photo_url: newUrl || d.profile_photo_url }));
      setPhotoVer(Date.now());
      toast.success('Photo updated!');
    } catch {} finally {
      setUploading(false);
      if (photoInput.current) photoInput.current.value = '';
    }
  }

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </WorkerLayout>
    );
  }

  if (!data?.expiry_date || new Date(data.expiry_date) < new Date()) {
    return (
      <WorkerLayout>
        <div className="text-center py-16 px-4">
          <span className="text-5xl">🔒</span>
          <h2 className="mt-4 font-bold text-gray-900 text-lg">Digital Card Locked</h2>
          <p className="text-gray-500 text-sm mt-2">Pay ₹10 Validation &amp; Verification fee to unlock your Digital Worker Card.</p>
          <button
            onClick={() => navigate('/worker/payment/registration')}
            className="mt-6 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors"
          >
            Pay ₹10 Now
          </button>
        </div>
      </WorkerLayout>
    );
  }

  // DB column is profile_photo_url; append cache-bust so browser reloads after upload
  const rawPhoto = data.profile_photo_url || data.photo_url;
  const photoUrl = rawPhoto ? `${imgSrc(rawPhoto)}?v=${photoVer}` : null;

  return (
    <WorkerLayout>
      <div className="max-w-sm mx-auto pb-24 sm:pb-8">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900">My Digital ID</h1>
          <p className="text-sm text-gray-500 mt-0.5">Share your verified worker profile with employers</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5">
          {[
            { key: 'card',      label: '🪪 ID Card' },
            { key: 'documents', label: '📁 Documents' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                tab === t.key
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'card' && (
          <div className="space-y-4">
            <IDCard data={data} photoUrl={photoUrl} />

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={copyLink}
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
              >
                📋 Copy Link
              </button>
              <button
                onClick={shareCard}
                className="flex items-center justify-center gap-2 bg-blue-700 text-white font-semibold text-sm py-2.5 rounded-xl hover:bg-blue-800 transition-colors shadow-sm"
              >
                ↗ Share Card
              </button>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Your Public Card URL</p>
              <p className="text-xs text-blue-700 font-mono break-all">{cardUrl}</p>
            </div>

            {/* Photo upload */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-semibold text-gray-900 text-sm">Profile Photo</p>
              <p className="text-xs text-gray-400 mt-0.5 mb-3">A clear face photo improves employer trust</p>
              <input
                ref={photoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <button
                onClick={() => photoInput.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 border border-blue-200 text-blue-700 bg-blue-50 font-semibold text-sm py-2.5 rounded-xl hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                {uploading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Uploading…
                  </>
                ) : (
                  rawPhoto ? '🔄 Change Photo' : '📷 Upload Photo'
                )}
              </button>
            </div>
          </div>
        )}

        {tab === 'documents' && <DocumentsSection />}
      </div>
    </WorkerLayout>
  );
}
